const BANNER_FRAME_ID = 'ug-banner-frame'
const BANNER_SPACER_ID = 'userguiding-banner-spacer'

function isBannerVisible(frame: HTMLElement | null): frame is HTMLElement {
  if (!frame) {
    return false
  }
  return getComputedStyle(frame).display !== 'none'
}

function visibleBannerHeight(frame: HTMLElement): number {
  const rect = frame.getBoundingClientRect()
  return Math.max(0, Math.min(rect.height, rect.bottom))
}

function isBannerAnimating(frame: HTMLElement): boolean {
  const cs = getComputedStyle(frame)
  const transform = cs.transform
  const identity =
    transform === 'none' || transform === 'matrix(1, 0, 0, 1, 0, 0)'
  const opacity = Number(cs.opacity)
  const target = parseFloat(frame.style.height)
  const rect = frame.getBoundingClientRect()
  const heightSettled =
    Number.isNaN(target) || Math.abs(rect.height - target) < 0.5
  const topSettled = Math.abs(rect.top) < 0.5
  const opacitySettled = opacity < 0.01 || opacity > 0.99
  return !identity || !heightSettled || !topSettled || !opacitySettled
}

function sidebarEl(doc: Document): HTMLElement | null {
  return (
    doc.querySelector<HTMLElement>('.ht-nav') ??
    doc.querySelector<HTMLElement>('nav')
  )
}

function clearPushedPaneHeight(doc: Document) {
  doc.querySelectorAll<HTMLElement>('.right-side').forEach(el => {
    el.style.removeProperty('height')
    el.style.removeProperty('min-height')
    el.style.removeProperty('max-height')
  })
}

function clearBannerContainingBlock(el: HTMLElement | null, active: boolean) {
  if (!el) {
    return
  }
  if (active) {
    el.style.setProperty('transform', 'none', 'important')
    el.style.setProperty('filter', 'none', 'important')
    el.style.setProperty('perspective', 'none', 'important')
    return
  }
  el.style.removeProperty('transform')
  el.style.removeProperty('filter')
  el.style.removeProperty('perspective')
}

export function syncUserGuidingBannerLayout(doc: Document = document): void {
  const root = doc.documentElement
  const frame = doc.getElementById(BANNER_FRAME_ID)
  const spacer = doc.getElementById(BANNER_SPACER_ID)
  const nav = sidebarEl(doc)
  const visible = isBannerVisible(frame)
  const height = visible ? visibleBannerHeight(frame) : 0
  const left =
    visible && nav ? Math.round(nav.getBoundingClientRect().width) : 0

  root.style.setProperty('--ug-banner-height', `${height}px`)
  root.style.setProperty('--ug-banner-left', `${left}px`)
  root.classList.toggle('ug-banner-visible', visible)

  if (spacer) {
    spacer.style.setProperty('display', 'none', 'important')
    spacer.style.setProperty('height', '0', 'important')
    spacer.style.setProperty('min-height', '0', 'important')
    spacer.style.setProperty('max-height', '0', 'important')
  }

  if (visible) {
    doc.body.style.setProperty('height', '100%', 'important')
  } else {
    doc.body.style.removeProperty('height')
  }

  clearBannerContainingBlock(root, visible)
  clearBannerContainingBlock(doc.body, visible)
  clearBannerContainingBlock(doc.querySelector('app-root'), visible)
  clearPushedPaneHeight(doc)
}

let boundDoc: Document | null = null

export function bindUserGuidingBannerLayout(
  doc: Document = document
): () => void {
  if (boundDoc === doc) {
    return () => undefined
  }
  boundDoc = doc

  let rafId = 0
  let applyingLayout = false

  const resizeObserver = new ResizeObserver(() => {
    syncAndTick()
  })
  const observed = new Set<Element>()

  const observe = (el: Element | null) => {
    if (!el || observed.has(el)) {
      return
    }
    observed.add(el)
    resizeObserver.observe(el)
    mutationObserver.observe(el, {
      attributes: true,
      attributeFilter: ['style'],
    })
  }

  const tick = () => {
    syncUserGuidingBannerLayout(doc)
    const frame = doc.getElementById(BANNER_FRAME_ID)
    if (frame && isBannerVisible(frame) && isBannerAnimating(frame)) {
      rafId = requestAnimationFrame(tick)
    }
  }

  const syncAndTick = () => {
    if (applyingLayout) {
      return
    }
    applyingLayout = true
    cancelAnimationFrame(rafId)
    syncUserGuidingBannerLayout(doc)
    observe(doc.getElementById(BANNER_FRAME_ID))
    observe(sidebarEl(doc))
    observe(doc.documentElement)
    observe(doc.body)
    observe(doc.querySelector('app-root'))
    observe(doc.querySelector('.right-side'))
    const frame = doc.getElementById(BANNER_FRAME_ID)
    if (frame && isBannerVisible(frame)) {
      rafId = requestAnimationFrame(tick)
    }
    applyingLayout = false
  }

  const mutationObserver = new MutationObserver(syncAndTick)
  mutationObserver.observe(doc.body, { childList: true, subtree: true })
  syncAndTick()

  return () => {
    cancelAnimationFrame(rafId)
    mutationObserver.disconnect()
    resizeObserver.disconnect()
    if (boundDoc === doc) {
      boundDoc = null
    }
  }
}
